import { useEffect, useState } from 'react';
import type { Track } from '../page';
import { getRadioConfig } from '../../../api/client';
import {
  DEFAULT_RADIO_CONFIG,
  RADIO_TRACK_SENTINEL_ID,
  mergeRadioConfig,
  type RadioConfig,
} from '../../../utils/radioConfig';

interface RadioStationProps {
  isRadioPlaying: boolean;
  setIsRadioPlaying: (playing: boolean) => void;
  setCurrentTrack: (track: Track) => void;
  setIsPlaying: (playing: boolean) => void;
}

export default function RadioStation({
  isRadioPlaying,
  setIsRadioPlaying,
  setCurrentTrack,
  setIsPlaying,
}: RadioStationProps) {
  const [config, setConfig] = useState<RadioConfig>(DEFAULT_RADIO_CONFIG);
  const [showSchedule, setShowSchedule] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { config: raw } = await getRadioConfig();
        if (!cancelled) setConfig(mergeRadioConfig(raw));
      } catch {
        if (!cancelled) setConfig(DEFAULT_RADIO_CONFIG);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentShow = config.currentShowTitle;

  const radioTrack: Track = {
    id: RADIO_TRACK_SENTINEL_ID,
    title: config.trackTitle,
    artist: config.trackArtist,
    album: config.trackAlbum,
    duration: config.trackDuration,
    image: config.trackImage,
    audioUrl: config.streamUrl?.trim() || undefined,
  };

  const handleRadioPlay = () => {
    const nowPlaying = !isRadioPlaying;
    setIsRadioPlaying(nowPlaying);
    setCurrentTrack(radioTrack);
    // Only start audio if a real stream URL is configured
    setIsPlaying(nowPlaying && !!radioTrack.audioUrl);
  };

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-[2rem] border border-stone-200/70 bg-[radial-gradient(circle_at_top_right,_rgba(251,191,36,0.18),_transparent_30%),linear-gradient(135deg,_#1c1917_0%,_#44403c_42%,_#78350f_100%)] p-6 text-white shadow-[0_16px_60px_rgba(120,53,15,0.18)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-200/80">Live radio</p>
            <h2 className="mt-2 text-2xl font-bold">{config.stationName}</h2>
            <p className="mt-2 max-w-sm text-sm leading-6 text-stone-200">{config.tagline}</p>
          </div>
          <div className="rounded-full border border-white/15 bg-white/10 px-3 py-2 text-xs font-medium uppercase tracking-wide text-amber-100">
            {config.listenerCount.toLocaleString()} listening
          </div>
        </div>

        <div className="mt-6 rounded-[1.5rem] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <img
              src={config.trackImage}
              alt="Current radio artwork"
              className="h-20 w-20 rounded-2xl object-cover shadow-lg"
            />
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-[0.22em] text-amber-200/70">On air now</p>
              <h3 className="mt-2 truncate text-xl font-semibold">{currentShow}</h3>
              <p className="mt-1 text-sm text-stone-200">{config.currentShowSubtitle}</p>
              <div className="mt-3 flex items-center gap-2 text-xs">
                {config.isLive !== false ? (
                  <>
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-emerald-200">{isRadioPlaying ? 'Broadcasting live' : 'On air — ready to play'}</span>
                  </>
                ) : (
                  <>
                    <span className="h-2 w-2 rounded-full bg-stone-500" />
                    <span className="text-stone-400">Station offline</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleRadioPlay}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition ${
              isRadioPlaying ? 'bg-white text-stone-900' : 'bg-amber-400 text-stone-950 hover:bg-amber-300'
            }`}
          >
            <i className={isRadioPlaying ? 'ri-pause-fill' : 'ri-play-fill'} />
            {isRadioPlaying ? 'Pause radio' : 'Play radio'}
          </button>
          <button
            type="button"
            onClick={() => setShowSchedule(!showSchedule)}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-3 text-sm font-medium text-white hover:bg-white/15"
          >
            <i className="ri-calendar-line" />
            {showSchedule ? 'Hide schedule' : 'View schedule'}
          </button>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-3 text-sm text-stone-200">
            <i className="ri-broadcast-line" />
            HD stream
          </div>
        </div>

        {showSchedule && (
          <div className="mt-6 rounded-[1.5rem] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-amber-100/85">Today&apos;s schedule</h3>
              <button type="button" onClick={() => setShowSchedule(false)} className="text-stone-300 hover:text-white">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="space-y-2">
              {config.schedule.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between rounded-2xl px-4 py-3 ${
                    item.show === currentShow ? 'bg-amber-300/15 text-white' : 'bg-white/5 text-stone-200'
                  }`}
                >
                  <div>
                    <p className="font-medium">{item.show}</p>
                    <p className="text-xs text-stone-300">with {item.host}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">{item.time}</p>
                    {item.show === currentShow && <p className="text-xs text-emerald-200">Current show</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-3xl border border-stone-200/70 bg-white/80 p-4 text-center shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Signal</p>
          <p className="mt-2 text-lg font-semibold text-stone-900">Strong</p>
        </div>
        <div className="rounded-3xl border border-stone-200/70 bg-white/80 p-4 text-center shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Quality</p>
          <p className="mt-2 text-lg font-semibold text-stone-900">HD</p>
        </div>
        <div className="rounded-3xl border border-stone-200/70 bg-white/80 p-4 text-center shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Listeners</p>
          <p className="mt-2 text-lg font-semibold text-stone-900">{config.listenerCount.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}
